import { Controller } from "@hotwired/stimulus"
import type FlatpickrController from "./flatpickr_controller"

/**
 * Stimulus controller for linking start/end date pickers.
 *
 * Ensures end date cannot be before start date by dynamically updating
 * the end date picker's minDate when the start date changes.
 *
 * Usage:
 *   <div data-controller="date-range"
 *        data-date-range-flatpickr-outlet="[data-controller='flatpickr']">
 *     <input data-controller="flatpickr" data-date-range-target="startDate" />
 *     <input data-controller="flatpickr" data-date-range-target="endDate" />
 *   </div>
 */
export default class DateRangeController extends Controller {
  static outlets = ["flatpickr"]
  static targets = ["startDate", "endDate"]

  declare flatpickrOutlets: FlatpickrController[]
  declare startDateTarget: HTMLInputElement
  declare endDateTarget: HTMLInputElement

  startDateTargetConnected() {
    this.startDateTarget.addEventListener("change", () => this.updateEndDateMin())
  }

  updateEndDateMin() {
    const startPicker = this.flatpickrOutlets.find(o => o.element === this.startDateTarget)
    const endPicker = this.flatpickrOutlets.find(o => o.element === this.endDateTarget)

    if (!startPicker?.instance || !endPicker?.instance) {
      return
    }

    // Get the selected date from the flatpickr instance
    // Use selectedDates if available, otherwise parse from the input value
    const startDate = startPicker.selectedDates[0]

    if (startDate) {
      endPicker.instance.set("minDate", startDate)

      // If current end date is before new start date, update it to match start date
      const endDate = endPicker.selectedDates[0]
      if (endDate && endDate < startDate) {
        endPicker.instance.setDate(startDate)
      }
    }
  }
}
